import { type ReactElement, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type KycCatalogVersionView, type KycCatalogView, type KycCatalogWire, type KycDocumentTypeWire, type KycRequirementBindingWire, type KycVehicleClassRuleWire, type KycVehicleRuleWire } from '@vello/api-client';
import { KYC_CATALOG_REASON_MAX, KYC_PERSONAS, kycTypeLabel } from '@vello/driver-eligibility';
import {
  maySelfApprove,
  adminScreenMarketChoices,
  cashHandoverActor,
  catalogDraftCategories,
  catalogDraftChanged,
  catalogDraftFrom,
  catalogDraftProblems,
  catalogLabelLanguages,
  decideKycCatalog,
  draftPutBinding,
  draftPutType,
  draftPutVehicleRule,
  draftRemoveBinding,
  draftRemoveVehicleRule,
  formatMarketInstant,
  kycBindingForm,
  kycBindingFromForm,
  kycCatalogApproveBlock,
  kycCatalogAuthority,
  kycCatalogCountryChoices,
  kycCatalogRejectable,
  kycCatalogStateTone,
  kycCatalogWithdrawable,
  kycTypeForm,
  kycTypeFromForm,
  kycVehicleRuleForm,
  kycVehicleRuleFromForm,
  loadKycCatalog,
  loadKycCatalogCountries,
  marketDisplayName,
  proposeKycCatalog,
  scopeTimeZone,
  type CatalogDraftProblem,
  type KycBindingForm,
  type KycCatalogDecision,
  type KycTypeForm,
  type KycVehicleRuleForm,
} from '../../src/index';
import { SelfApprovalNote } from '../components/self-approval-note';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { unregisteredDocumentType } from '../i18n/document-type';
import { useCountryName } from '../i18n/country-name';
import {
  Badge, BulletList, Button, CapWidth, Card, CheckRow, Cluster, EmptyState, Field, FormGrid, HistoryEntry, MetaLine, NoteText, Notice, PageHeader, RuleCard, RuleCardBody, RuleDetail, RuleGroup,
  RuleListPanel, RulePill, RuleRowButton, RulesWorkspace, RuleTitle, SectionTitle, Select, Stack, SubmitRow, Table, Text, TextInput,
} from '@vello/web-theme';
import { LoadingBlock } from '../components/loading-block';
import { usePersonNames } from '../components/person-name';

type Msg = { readonly tone: 'success' | 'danger' | 'warning'; readonly text: string };
/** D764: what the list on the left narrows the cards to — the whole catalog or one of its sections. */
type CatalogSection = 'all' | 'types' | 'requirements' | 'vehicleRules' | 'classRules';

/**
 * **Configurações › Documentos exigidos** (W8-DRIVER-ELIGIBILITY, D437, ADR-0069) — a country's KYC document-type catalog
 * under four eyes, over `GET/POST /v1/admin/kyc-catalog*` (`KycCatalogAdminApi`).
 *
 * - The **country** is chosen from the operator's reach (the countries of the markets its scope covers, plus — for a
 *   global operator — the countries that already have a catalog), never typed.
 * - The **version in force**: its document types (label from the catalog itself, whether it belongs to the person or to
 *   the vehicle, the rules as badges, the advance notice), its requirements (type, persona, country or market, all
 *   categories or one, required or optional, overridable) and the per-category vehicle rules.
 * - The **pending change**: who proposed it, why and when (in the country's zone when its markets share one, else UTC,
 *   labelled). Approve is HIDDEN from its proposer with the reason shown, Withdraw is the proposer's only; every decision
 *   needs a reason. The server compares the session identities anyway (`SELF_APPROVAL`, `NOT_PROPOSER`).
 * - The **editor**: a draft of the next version, started from the version in force — types, requirements and vehicle
 *   rules are edited in forms (never a JSON blob), the kernel's own validation lists every problem in words before
 *   anything is sent, and the whole draft is proposed with a reason (the screen's one primary action).
 * - The **history** of versions. A refused read is an error, never an empty catalog. No native dialog is ever opened.
 * - D764: laid out in the grammar of Regras governadas (Claude Design A12, D762) — the catalog's sections on the left (the whole
 *   catalog, document types, requirements, vehicle rules, vehicle classes; each says what the draft or the proposal changes there),
 *   and on the right the change awaiting approval, the version in force, the editor and the history, narrowed to the section.
 */
export function KycCatalogScreen(): ReactElement {
  const { runtime, scope, markets, me, grants } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { t, tp, lang } = useT();
  const { fail, v, refusal: refusalOf } = useVocab();
  const countryName = useCountryName();
  const operatorId = me?.id;
  const { mayPropose, mayApprove } = kycCatalogAuthority(grants);
  const marketChoices = useMemo(() => adminScreenMarketChoices(scope, markets), [scope, markets]);
  const [listed, setListed] = useState<readonly string[]>([]);
  const countries = useMemo(() => kycCatalogCountryChoices(marketChoices.map((m) => m.id), scope.current.country, listed), [marketChoices, scope, listed]);
  // D580: the top-bar scope fixes the country; the picker exists only where the scope spans countries (a platform-level operator).
  const scopeCountry = scope.current.country?.toUpperCase();
  const [pickedCountry, setCountry] = useState<string>('');
  const country = scopeCountry ?? pickedCountry;
  const [view, setView] = useState<KycCatalogView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<Msg | undefined>(undefined);
  const [decisionReason, setDecisionReason] = useState('');
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  const [draft, setDraft] = useState<KycCatalogWire | undefined>(undefined);
  const [typeEdit, setTypeEdit] = useState<{ readonly index?: number; readonly form: KycTypeForm } | undefined>(undefined);
  const [bindingEdit, setBindingEdit] = useState<{ readonly index?: number; readonly form: KycBindingForm } | undefined>(undefined);
  const [ruleEdit, setRuleEdit] = useState<{ readonly index?: number; readonly form: KycVehicleRuleForm } | undefined>(undefined);
  const [proposalReason, setProposalReason] = useState('');
  const [proposeMsg, setProposeMsg] = useState<Msg | undefined>(undefined);
  const [section, setSection] = useState<CatalogSection>('all');

  const refusal = (e: { reasonCode?: string; detail?: string; code: string }, fallbackKey: string): string => {
    return refusalOf('refusal.admin.driver_enablement', e) ?? t(fallbackKey, { code: fail(e) });
  };

  const load = useCallback(
    async (c: string): Promise<void> => {
      const n = ++seq.current;
      setError(undefined);
      setView(undefined);
      setDraft(undefined);
      setTypeEdit(undefined);
      setBindingEdit(undefined);
      setRuleEdit(undefined);
      if (c === '') return;
      setLoading(true);
      const res = await loadKycCatalog(runtime, c);
      if (n !== seq.current) return; // another country was asked for since: its answer governs
      setLoading(false);
      if (!res.ok) {
        setError(t('denable.catalog.error', { code: fail(res.error) }));
        return;
      }
      setView(res.value);
      setDraft(catalogDraftFrom(res.value, c));
    },
    [runtime, t],
  );

  useEffect(() => {
    // The countries that already have a catalog: a GLOBAL read (a country operator is refused and keeps its scope's).
    void loadKycCatalogCountries(runtime).then((res) => {
      if (res.ok) setListed(res.value.countries);
    });
  }, [runtime]);

  useEffect(() => {
    if (country === '' && countries.length === 1) setCountry(countries[0] ?? '');
  }, [country, countries]);

  useEffect(() => {
    void load(country);
  }, [country, load]);

  const tz = scopeTimeZone({ country }, markets);
  const when = (instant: string | undefined): string => formatMarketInstant(instant, tz, lang);
  const personName = usePersonNames([view?.inForce, view?.pending, ...(view?.history ?? [])].flatMap((ver) => (ver == null ? [] : [ver.proposedBy, ver.decidedBy])));
  const who = (id: string | undefined): string => {
    const actor = cashHandoverActor(id ?? null, undefined, operatorId);
    return actor === undefined ? '—' : actor.kind === 'you' ? t('denable.actor.you') : (personName(id) ?? actor.text);
  };
  const typeName = (catalog: KycCatalogWire, code: string): string => {
    const type = catalog.types.find((x) => x.code === code);
    return type !== undefined ? kycTypeLabel(type, lang) : unregisteredDocumentType(v);
  };
  const scopeName = (b: KycRequirementBindingWire): string => (b.scope === 'market' && b.marketId !== undefined ? marketDisplayName(b.marketId, markets) : t('denable.scope.country'));

  const decide = async (change: KycCatalogVersionView, action: KycCatalogDecision): Promise<void> => {
    setBusy(true);
    setMsg(undefined);
    const res = await decideKycCatalog(runtime, change, action, decisionReason);
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: refusal(res.error, 'ui.actionFailed') });
      if (res.error.status === 409) await load(country);
      return;
    }
    setMsg({ tone: 'success', text: t(`denable.catalog.decided.${action}`, { version: String(change.version) }) });
    setDecisionReason('');
    await load(country);
  };

  const inForce = view?.inForce ?? null;
  const pending = view?.pending ?? null;
  const problems = draft !== undefined ? catalogDraftProblems(draft) : [];
  const changed = draft !== undefined && catalogDraftChanged(draft, inForce?.catalog);
  const canPropose = draft !== undefined && pending === null && changed && problems.length === 0 && proposalReason.trim() !== '' && !busy;
  const countryMarkets = markets.filter((m) => m.countryCode.toUpperCase() === country && m.status !== 'RETIRED');

  const propose = async (): Promise<void> => {
    if (draft === undefined || !canPropose) return;
    setBusy(true);
    setProposeMsg(undefined);
    const res = await proposeKycCatalog(runtime, { draft, baseVersion: inForce?.version, reason: proposalReason });
    setBusy(false);
    if (!res.ok) {
      setProposeMsg({ tone: 'danger', text: refusal(res.error, 'ui.actionFailed') });
      return;
    }
    setProposalReason('');
    await load(country);
    setProposeMsg({ tone: 'success', text: t('denable.editor.proposed', { version: String(res.value.version) }) });
  };

  const problemWhere = (p: CatalogDraftProblem): string => {
    if (draft === undefined || p.index === undefined) return t('denable.problem.where.catalog');
    if (p.section === 'types') {
      const type = draft.types[p.index];
      return t('denable.problem.where.type', { name: type !== undefined ? (type.code !== '' ? kycTypeLabel(type, lang) : '—') : String(p.index + 1) });
    }
    if (p.section === 'requirements') {
      const b = draft.requirements[p.index];
      return t('denable.problem.where.requirement', { n: String(p.index + 1), name: b !== undefined ? typeName(draft, b.documentType) : '—' });
    }
    const r = draft.vehicleRules[p.index];
    return t('denable.problem.where.vehicleRule', { name: r?.category !== undefined && r.category !== '' ? v('ride.category', r.category) : String(p.index + 1) });
  };

  const rulesBadges = (type: KycDocumentTypeWire): ReactNode => (
    <Cluster gap={2} wrap>
      {type.rules.number && <Badge>{t('denable.rule.number')}</Badge>}
      {type.rules.expiry && <Badge>{t('denable.rule.expiry')}</Badge>}
      {type.rules.frontBack && <Badge>{t('denable.rule.frontBack')}</Badge>}
      {type.rules.photo && <Badge>{t('denable.rule.photo')}</Badge>}
      {!type.rules.number && !type.rules.expiry && !type.rules.frontBack && !type.rules.photo && <Text role="caption" tone="tertiary" block>—</Text>}
    </Cluster>
  );

  /** Owner answer (PR 291 question 5): the courier vehicle classes the catalog exempts — shown as they are (read only here). */
  const classRulesTable = (catalog: KycCatalogWire): ReactElement => (
    <>
      <Text as="p" role="caption" tone="tertiary">{t('denable.classRules.lead')}</Text>
      <Table
        columns={[
          { key: 'class', header: t('denable.classRules.col.class'), render: (r: KycVehicleClassRuleWire) => <span>{v('kyc.inspection_vehicle_class', r.vehicleClass)}<Text role="caption" tone="tertiary" block mono>{r.vehicleClass}</Text></span> },
          { key: 'inspection', header: t('denable.classRules.col.inspection'), render: (r: KycVehicleClassRuleWire) => <Badge tone={r.requiresInspection ? 'neutral' : 'warning'}>{t(r.requiresInspection ? 'denable.classRules.required' : 'denable.classRules.exempt')}</Badge> },
          { key: 'documents', header: t('denable.classRules.col.documents'), render: (r: KycVehicleClassRuleWire) => <Badge tone={r.requiresVehicleDocuments ? 'neutral' : 'warning'}>{t(r.requiresVehicleDocuments ? 'denable.classRules.required' : 'denable.classRules.exempt')}</Badge> },
        ]}
        rows={catalog.vehicleClassRules ?? []}
        rowKey={(r) => r.vehicleClass}
        empty={<EmptyState title={t('denable.classRules.empty')} />}
      />
    </>
  );

  const typesTable = (catalog: KycCatalogWire, onEdit?: (index: number) => void): ReactElement => (
    <Table
      columns={[
        { key: 'label', header: t('denable.col.type'), render: (x: KycDocumentTypeWire) => <span>{kycTypeLabel(x, lang)}<Text role="caption" tone="tertiary" block mono>{x.code}</Text></span> },
        { key: 'subject', header: t('denable.col.subject'), render: (x: KycDocumentTypeWire) => v('kyc.document_subject', x.subject) },
        { key: 'rules', header: t('denable.col.rules'), render: rulesBadges },
        { key: 'notice', header: t('denable.col.notice'), numeric: true, render: (x: KycDocumentTypeWire) => <span className="tnum">{x.expiryNoticeDays !== undefined ? t('denable.noticeDays', { n: String(x.expiryNoticeDays) }) : '—'}</span> },
        { key: 'fromIssue', header: t('denable.col.validityFromIssue'), numeric: true, render: (x: KycDocumentTypeWire) => <span className="tnum">{x.validityFromIssue !== undefined ? (x.validityFromIssue.unit === 'month' ? tp('denable.validityFromIssue.month', x.validityFromIssue.amount) : tp('denable.validityFromIssue.day', x.validityFromIssue.amount)) : '—'}</span> },
        { key: 'active', header: t('denable.col.active'), render: (x: KycDocumentTypeWire) => <Badge tone={x.active ? 'success' : 'neutral'}>{t(x.active ? 'denable.type.active' : 'denable.type.retired')}</Badge> },
        ...(onEdit !== undefined
          ? [{ key: 'act', header: '', render: (x: KycDocumentTypeWire) => <Button size="sm" variant="secondary" onClick={() => onEdit(catalog.types.indexOf(x))}>{t('denable.editor.edit')}</Button> }]
          : []),
      ]}
      rows={catalog.types}
      rowKey={(x) => x.code === '' ? `new-${catalog.types.indexOf(x)}` : `${x.code}-${catalog.types.indexOf(x)}`}
      empty={<EmptyState title={t('denable.types.empty')} />}
    />
  );

  type IndexedBinding = { readonly b: KycRequirementBindingWire; readonly i: number };
  const bindingsTable = (catalog: KycCatalogWire, actions?: { edit: (i: number) => void; remove: (i: number) => void }): ReactElement => (
    <Table
      columns={[
        { key: 'type', header: t('denable.col.type'), render: ({ b }: IndexedBinding) => typeName(catalog, b.documentType) },
        { key: 'persona', header: t('denable.col.persona'), render: ({ b }: IndexedBinding) => v('kyc.persona', b.persona) },
        { key: 'scope', header: t('denable.col.scope'), render: ({ b }: IndexedBinding) => scopeName(b) },
        { key: 'category', header: t('denable.col.category'), render: ({ b }: IndexedBinding) => (b.category !== undefined ? <span>{v('ride.category', b.category)}</span> : t('denable.category.all')) },
        { key: 'level', header: t('denable.col.level'), render: ({ b }: IndexedBinding) => <Badge tone={b.requirement === 'REQUIRED' ? 'warning' : 'neutral'}>{v('kyc.requirement_level', b.requirement)}</Badge> },
        { key: 'overridable', header: t('denable.col.overridable'), render: ({ b }: IndexedBinding) => (b.overridable === true ? t('denable.overridable.yes') : <Text role="caption" tone="tertiary" block>—</Text>) },
        ...(actions !== undefined
          ? [{
            key: 'act',
            header: '',
            render: ({ i }: IndexedBinding) => (
              <Cluster gap={2} wrap>
                <Button size="sm" variant="secondary" onClick={() => actions.edit(i)}>{t('denable.editor.edit')}</Button>
                <Button size="sm" variant="danger" onClick={() => actions.remove(i)}>{t('denable.editor.remove')}</Button>
              </Cluster>
            ),
          }]
          : []),
      ]}
      rows={catalog.requirements.map((b, i) => ({ b, i }))}
      rowKey={({ i }) => `req-${i}`}
      empty={<EmptyState title={t('denable.requirements.empty')} />}
    />
  );

  type IndexedRule = { readonly r: KycVehicleRuleWire; readonly i: number };
  const rulesTable = (catalog: KycCatalogWire, actions?: { edit: (i: number) => void; remove: (i: number) => void }): ReactElement => (
    <Table
      columns={[
        { key: 'category', header: t('denable.col.category'), render: ({ r }: IndexedRule) => <span>{v('ride.category', r.category)}</span> },
        { key: 'classes', header: t('denable.col.vehicleClasses'), render: ({ r }: IndexedRule) => (r.rule.allowedVehicleTypes !== undefined ? r.rule.allowedVehicleTypes.map((vt) => v('service_categories.vehicle_class', vt)).join(', ') : t('denable.vehicleClasses.any')) },
        { key: 'year', header: t('denable.col.minYear'), numeric: true, render: ({ r }: IndexedRule) => <span className="tnum">{r.rule.minVehicleYear ?? '—'}</span> },
        {
          key: 'flags',
          header: t('denable.col.vehicleChecks'),
          render: ({ r }: IndexedRule) => (
            <Cluster gap={2} wrap>
              {r.rule.requireValidInspection === true && <Badge>{t('denable.vehicleRule.inspection')}</Badge>}
              {r.rule.requireActiveVehicle === true && <Badge>{t('denable.vehicleRule.active')}</Badge>}
            </Cluster>
          ),
        },
        ...(actions !== undefined
          ? [{
            key: 'act',
            header: '',
            render: ({ i }: IndexedRule) => (
              <Cluster gap={2} wrap>
                <Button size="sm" variant="secondary" onClick={() => actions.edit(i)}>{t('denable.editor.edit')}</Button>
                <Button size="sm" variant="danger" onClick={() => actions.remove(i)}>{t('denable.editor.remove')}</Button>
              </Cluster>
            ),
          }]
          : []),
      ]}
      rows={catalog.vehicleRules.map((r, i) => ({ r, i }))}
      rowKey={({ i }) => `rule-${i}`}
      empty={<EmptyState title={t('denable.vehicleRules.empty')} />}
    />
  );

  const block = pending !== null ? kycCatalogApproveBlock(pending, operatorId, mayApprove, selfApproval) : undefined;
  const hasDecisionReason = decisionReason.trim() !== '';
  const languages = draft !== undefined ? catalogLabelLanguages(draft, lang) : [];
  const draftCategories = draft !== undefined ? catalogDraftCategories(draft) : [];

  const SECTIONS: readonly CatalogSection[] = ['all', 'types', 'requirements', 'vehicleRules', 'classRules'];
  const shows = (x: Exclude<CatalogSection, 'all'>): boolean => section === 'all' || section === x;
  const sectionTitle = (x: CatalogSection): string => t(x === 'all' ? 'denable.gr.all' : x === 'types' ? 'denable.types.title' : x === 'requirements' ? 'denable.requirements.title' : x === 'vehicleRules' ? 'denable.vehicleRules.title' : 'denable.classRules.title');
  const countOf = (c: KycCatalogWire | undefined, x: CatalogSection): number => (c === undefined ? 0 : x === 'all' ? c.types.length + c.requirements.length + c.vehicleRules.length : x === 'types' ? c.types.length : x === 'requirements' ? c.requirements.length : x === 'vehicleRules' ? c.vehicleRules.length : (c.vehicleClassRules ?? []).length);
  const sectionOf = (c: KycCatalogWire | undefined, x: CatalogSection): unknown => (c === undefined ? undefined : x === 'all' ? c : x === 'types' ? c.types : x === 'requirements' ? c.requirements : x === 'vehicleRules' ? c.vehicleRules : c.vehicleClassRules ?? []);
  /** Whether a catalog differs from the one in force in a section (what the proposal or the draft would change there). */
  const differs = (c: KycCatalogWire | undefined, x: CatalogSection): boolean => c !== undefined && JSON.stringify(sectionOf(c, x)) !== JSON.stringify(sectionOf(inForce?.catalog, x));

  /** The catalog's sections, filtered by the list on the left (every section by default). */
  const catalogTables = (catalog: KycCatalogWire): ReactElement => (
    <Stack gap={3}>
      {shows('types') && <><Text as="h3" role="label">{t('denable.types.title')}</Text>{typesTable(catalog)}</>}
      {shows('requirements') && <><Text as="h3" role="label">{t('denable.requirements.title')}</Text>{bindingsTable(catalog)}</>}
      {shows('vehicleRules') && <><Text as="h3" role="label">{t('denable.vehicleRules.title')}</Text>{rulesTable(catalog)}</>}
      {shows('classRules') && <><Text as="h3" role="label">{t('denable.classRules.title')}</Text>{classRulesTable(catalog)}</>}
    </Stack>
  );

  const listPanel = (
    <RuleListPanel label={t('denable.gr.sections')} head={<Text role="caption" tone="secondary">{t('denable.catalog.explain')}</Text>}>
      <RuleGroup name={country === '' ? t('denable.country') : countryName(country)} count={SECTIONS.length - 1}>
        {SECTIONS.map((x) => (
          <RuleRowButton
            key={x}
            testId={`kyc-section-${x}`}
            name={sectionTitle(x)}
            ruleKey={t('denable.gr.count', { n: String(countOf(inForce?.catalog, x)) })}
            source={inForce === null ? 'none' : 'own'}
            sourceLabel={draft !== undefined && differs(draft, x) ? t('denable.gr.draftChanged') : inForce === null ? t('denable.gr.noneInForce') : t('denable.gr.inForce')}
            {...(pending !== null && differs(pending.catalog, x) ? { pendingLabel: t('denable.gr.inApproval') } : {})}
            selected={x === section}
            onClick={() => setSection(x)}
          />
        ))}
      </RuleGroup>
    </RuleListPanel>
  );

  const detail = (
    <RuleDetail label={t('denable.catalog.title')}>
      {error !== undefined && <Notice tone="danger">{error}</Notice>}
      {loading && error === undefined && <LoadingBlock lines={2} />}
      {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}

      {view !== undefined && (
        <>
          <RuleTitle domain={countryName(country)} name={sectionTitle(section)} />

          {pending !== null && (
            <RuleCard tone="warning" title={t('denable.pending.title')} sub={t('denable.gr.pendingSub')}>
              <MetaLine>
                <span>{t('denable.pending.meta', { version: String(pending.version), by: who(pending.proposedBy), at: when(pending.proposedAt) })}</span>
              </MetaLine>
              <Text as="p">{t('denable.pending.why', { reason: pending.reason })}</Text>
              <Text as="p" role="caption" tone="tertiary">
                {t('denable.pending.counts', { types: String(pending.catalog.types.length), requirements: String(pending.catalog.requirements.length), rules: String(pending.catalog.vehicleRules.length) })}
              </Text>
              {catalogTables(pending.catalog)}
              <CapWidth size="lg">
                <Field label={t('denable.decision.reason')} htmlFor="kyc-decision-reason" hint={t('denable.decision.reason.hint')}>
                  <TextInput id="kyc-decision-reason" value={decisionReason} maxLength={KYC_CATALOG_REASON_MAX} onChange={(e) => setDecisionReason(e.target.value)} />
                </Field>
              </CapWidth>
              <Cluster gap={2} wrap>
                {kycCatalogWithdrawable(pending, operatorId, mayPropose) && (
                  <Button variant="ghost" disabled={busy || !hasDecisionReason} onClick={() => void decide(pending, 'withdraw')}>{t('denable.decision.withdraw')}</Button>
                )}
                {kycCatalogRejectable(pending, operatorId, mayApprove) && (
                  <Button variant="danger" disabled={busy || !hasDecisionReason} onClick={() => void decide(pending, 'reject')}>{t('denable.decision.reject')}</Button>
                )}
                {block === undefined && (
                  <Button variant="secondary" icon="check" disabled={busy || !hasDecisionReason} onClick={() => void decide(pending, 'approve')}>{t('denable.decision.approve')}</Button>
                )}
              </Cluster>
              {block !== undefined && block !== 'NOT_PENDING' && (
                <NoteText>{v('kyc.catalog_decision_block', block)}</NoteText>
              )}
            </RuleCard>
          )}

          <RuleCard title={t('denable.inForce.title')} aside={inForce !== null ? <Text role="caption" tone="tertiary" mono>{`v${String(inForce.version)}`}</Text> : undefined}>
            {inForce === null ? (
              <Notice tone="warning">{t('denable.inForce.none')}</Notice>
            ) : (
              <>
                <MetaLine>
                  <span>{t('denable.inForce.meta', { version: String(inForce.version), since: when(inForce.effectiveFrom), by: who(inForce.decidedBy) })}</span>
                </MetaLine>
                {catalogTables(inForce.catalog)}
              </>
            )}
          </RuleCard>

          <RuleCard title={t('denable.editor.title')} sub={t('denable.editor.explain')}>
            {!mayPropose ? (
              <Notice tone="neutral">{t('denable.editor.noPermission')}</Notice>
            ) : draft === undefined ? null : (
              <>
                {pending !== null && <Notice tone="neutral">{t('denable.editor.pendingExists')}</Notice>}

                {shows('types') && (
                  <>
                    <Text as="h3" role="label">{t('denable.types.title')}</Text>
                    {typesTable(draft, (i) => {
                      setTypeEdit({ index: i, form: kycTypeForm(draft.types[i], languages) });
                    })}
                    <Cluster gap={2} wrap>
                      <Button size="sm" variant="secondary" onClick={() => setTypeEdit({ form: kycTypeForm(undefined, languages) })}>{t('denable.editor.addType')}</Button>
                    </Cluster>
                    {typeEdit !== undefined && (
                      <TypeEditor
                        form={typeEdit.form}
                        isNew={typeEdit.index === undefined}
                        codeLocked={typeEdit.index !== undefined && (inForce?.catalog.types ?? []).some((x) => x.code === draft.types[typeEdit.index ?? -1]?.code)}
                        onChange={(form) => setTypeEdit({ ...typeEdit, form })}
                        onApply={() => {
                          setDraft(draftPutType(draft, kycTypeFromForm(typeEdit.form), typeEdit.index));
                          setTypeEdit(undefined);
                        }}
                        onCancel={() => setTypeEdit(undefined)}
                      />
                    )}
                  </>
                )}

                {shows('requirements') && (
                  <>
                    <Text as="h3" role="label">{t('denable.requirements.title')}</Text>
                    {bindingsTable(draft, {
                      edit: (i) => setBindingEdit({ index: i, form: kycBindingForm(draft.requirements[i]) }),
                      remove: (i) => setDraft(draftRemoveBinding(draft, i)),
                    })}
                    <Cluster gap={2} wrap>
                      <Button size="sm" variant="secondary" disabled={draft.types.length === 0} onClick={() => setBindingEdit({ form: kycBindingForm(undefined, draft.types[0]?.code ?? '') })}>{t('denable.editor.addRequirement')}</Button>
                    </Cluster>
                    {bindingEdit !== undefined && (
                      <BindingEditor
                        form={bindingEdit.form}
                        types={draft.types}
                        markets={countryMarkets.map((m) => ({ id: m.id, name: m.name }))}
                        categories={draftCategories}
                        onChange={(form) => setBindingEdit({ ...bindingEdit, form })}
                        onApply={() => {
                          setDraft(draftPutBinding(draft, kycBindingFromForm(bindingEdit.form), bindingEdit.index));
                          setBindingEdit(undefined);
                        }}
                        onCancel={() => setBindingEdit(undefined)}
                      />
                    )}
                  </>
                )}

                {shows('vehicleRules') && (
                  <>
                    <Text as="h3" role="label">{t('denable.vehicleRules.title')}</Text>
                    {rulesTable(draft, {
                      edit: (i) => setRuleEdit({ index: i, form: kycVehicleRuleForm(draft.vehicleRules[i]) }),
                      remove: (i) => setDraft(draftRemoveVehicleRule(draft, i)),
                    })}
                    <Cluster gap={2} wrap>
                      <Button size="sm" variant="secondary" onClick={() => setRuleEdit({ form: kycVehicleRuleForm(undefined) })}>{t('denable.editor.addVehicleRule')}</Button>
                    </Cluster>
                    {ruleEdit !== undefined && (
                      <VehicleRuleEditor
                        form={ruleEdit.form}
                        onChange={(form) => setRuleEdit({ ...ruleEdit, form })}
                        onApply={() => {
                          const base = ruleEdit.index !== undefined ? draft.vehicleRules[ruleEdit.index] : undefined;
                          setDraft(draftPutVehicleRule(draft, kycVehicleRuleFromForm(ruleEdit.form, base), ruleEdit.index));
                          setRuleEdit(undefined);
                        }}
                        onCancel={() => setRuleEdit(undefined)}
                      />
                    )}
                  </>
                )}

                {shows('classRules') && (
                  <>
                    <Text as="h3" role="label">{t('denable.classRules.title')}</Text>
                    {classRulesTable(draft)}
                  </>
                )}

                {problems.length > 0 && (
                  <Notice tone="warning">
                    <Text role="label" block>{t('denable.problems.title', { n: String(problems.length) })}</Text>
                    <BulletList>
                      {problems.map((p) => (
                        <li key={`${p.path}-${p.code}`}>{t('denable.problem.line', { where: problemWhere(p), problem: t(p.messageId) })}</li>
                      ))}
                    </BulletList>
                  </Notice>
                )}
                <CapWidth size="lg">
                  <Field label={t('denable.editor.reason')} htmlFor="kyc-proposal-reason" hint={t('denable.editor.reason.hint')}>
                    <TextInput id="kyc-proposal-reason" value={proposalReason} maxLength={KYC_CATALOG_REASON_MAX} onChange={(e) => setProposalReason(e.target.value)} />
                  </Field>
                </CapWidth>
                {proposeMsg !== undefined && <Notice tone={proposeMsg.tone}>{proposeMsg.text}</Notice>}
                <SubmitRow
                  action={<Button variant="primary" disabled={!canPropose} onClick={() => void propose()}>{t('denable.editor.propose')}</Button>}
                  hint={!changed ? t('denable.editor.unchanged') : problems.length > 0 ? t('denable.gr.hint.problems') : pending !== null ? t('denable.gr.hint.pending') : proposalReason.trim() === '' ? t('denable.gr.hint.reason') : t('denable.gr.hint.ready')}
                />
              </>
            )}
            {!mayPropose && proposeMsg !== undefined && <Notice tone={proposeMsg.tone}>{proposeMsg.text}</Notice>}
          </RuleCard>

          <RuleCard title={t('denable.history.title')} aside={<RulePill>{String(view.history.length)}</RulePill>} flush>
            {view.history.length === 0 ? (
              <RuleCardBody><Text role="caption" tone="tertiary">{t('denable.history.empty')}</Text></RuleCardBody>
            ) : view.history.map((ver) => (
              <HistoryEntry
                key={`${ver.country}-${ver.version}`}
                title={`v${String(ver.version)}`}
                pills={<RulePill tone={kycCatalogStateTone(ver.state) === 'success' ? 'success' : kycCatalogStateTone(ver.state) === 'warning' ? 'warning' : kycCatalogStateTone(ver.state) === 'danger' ? 'danger' : 'neutral'}>{v('kyc.catalog_change_state', ver.state)}</RulePill>}
                when={when(ver.effectiveFrom ?? ver.decidedAt ?? ver.proposedAt)}
                value={ver.reason}
                by={<>{who(ver.proposedBy)}{ver.decidedBy !== undefined ? ` · ${who(ver.decidedBy)}` : ver.state === 'PENDING' ? ` · ${t('denable.pending.waiting')}` : ''}</>}
              >
                {ver.decisionReason !== undefined && <Text role="caption" tone="tertiary" block>{ver.decisionReason}</Text>}
                <SelfApprovalNote record={ver} />
              </HistoryEntry>
            ))}
          </RuleCard>
        </>
      )}
    </RuleDetail>
  );

  return (
    <>
      <PageHeader
        title={t('denable.catalog.title')}
        subtitle={t('denable.catalog.subtitle')}
        {...(scopeCountry === undefined ? {
          actions: (
            <Field label={t('denable.country')} htmlFor="kyc-country" hint={t('denable.country.hint')}>
              <Select id="kyc-country" value={country} onChange={(e) => setCountry(e.target.value)} disabled={countries.length === 0}>
                <option value="">{t(countries.length === 0 ? 'denable.country.none' : 'denable.country.choose')}</option>
                {countries.map((c) => (
                  <option key={c} value={c}>{countryName(c)}</option>
                ))}
              </Select>
            </Field>
          ),
        } : {})}
      />
      <RulesWorkspace list={listPanel} detail={detail} />
    </>
  );
}

function EditorFrame({ title, children, onApply, onCancel }: { title: string; children: ReactNode; onApply: () => void; onCancel: () => void }): ReactElement {
  const { t } = useT();
  return (
    <Card as="div" role="group" aria-label={title}>
      <Stack gap={3}>
        <SectionTitle level={4} size="sub">{title}</SectionTitle>
        {children}
        <Cluster gap={2} wrap>
          <Button size="sm" variant="secondary" onClick={onApply}>{t('denable.editor.apply')}</Button>
          <Button size="sm" variant="ghost" onClick={onCancel}>{t('denable.editor.cancel')}</Button>
        </Cluster>
      </Stack>
    </Card>
  );
}

function TypeEditor({ form, isNew, codeLocked, onChange, onApply, onCancel }: { form: KycTypeForm; isNew: boolean; codeLocked: boolean; onChange: (form: KycTypeForm) => void; onApply: () => void; onCancel: () => void }): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  const set = (patch: Partial<KycTypeForm>): void => onChange({ ...form, ...patch });
  return (
    <EditorFrame title={t(isNew ? 'denable.editor.newType' : 'denable.editor.editType')} onApply={onApply} onCancel={onCancel}>
      <FormGrid>
        <Field label={t('denable.type.code')} htmlFor="kyc-type-code" hint={t('denable.type.code.hint')}>
          <TextInput id="kyc-type-code" className="tnum" value={form.code} disabled={codeLocked} autoComplete="off" spellCheck={false} onChange={(e) => set({ code: e.target.value })} />
        </Field>
        <Field label={t('denable.col.subject')} htmlFor="kyc-type-subject">
          <Select id="kyc-type-subject" value={form.subject} onChange={(e) => set({ subject: e.target.value as KycTypeForm['subject'] })}>
            <option value="PERSON">{v('kyc.document_subject', 'PERSON')}</option>
            <option value="VEHICLE">{v('kyc.document_subject', 'VEHICLE')}</option>
          </Select>
        </Field>
        <Field label={t('denable.type.notice')} htmlFor="kyc-type-notice" hint={t('denable.type.notice.hint')}>
          <TextInput id="kyc-type-notice" className="tnum" inputMode="numeric" value={form.noticeDays} onChange={(e) => set({ noticeDays: e.target.value })} />
        </Field>
        <Field label={t('denable.type.validityAmount')} htmlFor="kyc-type-validity" hint={t('denable.type.validityAmount.hint')}>
          <TextInput id="kyc-type-validity" className="tnum" inputMode="numeric" value={form.validityAmount} onChange={(e) => set({ validityAmount: e.target.value })} />
        </Field>
        <Field label={t('denable.type.validityUnit')} htmlFor="kyc-type-validity-unit">
          <Select id="kyc-type-validity-unit" value={form.validityUnit} onChange={(e) => set({ validityUnit: e.target.value as KycTypeForm['validityUnit'] })}>
            <option value="day">{t('denable.unit.day')}</option>
            <option value="month">{t('denable.unit.month')}</option>
          </Select>
        </Field>
      </FormGrid>
      <Cluster gap={2} wrap>
        <CheckRow id="kyc-type-number" checked={form.number} onChange={(v) => set({ number: v })}>{t('denable.rule.number')}</CheckRow>
        <CheckRow id="kyc-type-expiry" checked={form.expiry} onChange={(v) => set({ expiry: v })}>{t('denable.rule.expiry')}</CheckRow>
        <CheckRow id="kyc-type-frontback" checked={form.frontBack} onChange={(v) => set({ frontBack: v })}>{t('denable.rule.frontBack')}</CheckRow>
        <CheckRow id="kyc-type-photo" checked={form.photo} onChange={(v) => set({ photo: v })}>{t('denable.rule.photo')}</CheckRow>
        <CheckRow id="kyc-type-active" checked={form.active} onChange={(v) => set({ active: v })}>{t('denable.type.active')}</CheckRow>
      </Cluster>
      <FormGrid>
        {Object.keys(form.labels).sort().map((l) => (
          <Field key={l} label={t('denable.type.label', { lang: l })} htmlFor={`kyc-type-label-${l}`}>
            <TextInput id={`kyc-type-label-${l}`} value={form.labels[l] ?? ''} maxLength={80} onChange={(e) => set({ labels: { ...form.labels, [l]: e.target.value } })} />
          </Field>
        ))}
      </FormGrid>
    </EditorFrame>
  );
}

function BindingEditor({ form, types, markets, categories, onChange, onApply, onCancel }: { form: KycBindingForm; types: readonly KycDocumentTypeWire[]; markets: readonly { id: string; name: string }[]; categories: readonly string[]; onChange: (form: KycBindingForm) => void; onApply: () => void; onCancel: () => void }): ReactElement {
  const { t, lang } = useT();
  const { v } = useVocab();
  const set = (patch: Partial<KycBindingForm>): void => onChange({ ...form, ...patch });
  return (
    <EditorFrame title={t('denable.editor.requirement')} onApply={onApply} onCancel={onCancel}>
      <FormGrid>
        <Field label={t('denable.col.type')} htmlFor="kyc-binding-type">
          <Select id="kyc-binding-type" value={form.documentType} onChange={(e) => set({ documentType: e.target.value })}>
            {types.map((x) => (
              <option key={x.code} value={x.code}>{kycTypeLabel(x, lang)}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('denable.col.persona')} htmlFor="kyc-binding-persona">
          <Select id="kyc-binding-persona" value={form.persona} onChange={(e) => set({ persona: e.target.value as KycBindingForm['persona'] })}>
            {KYC_PERSONAS.map((p) => (
              <option key={p} value={p}>{v('kyc.persona', p)}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('denable.col.scope')} htmlFor="kyc-binding-scope">
          <Select id="kyc-binding-scope" value={form.scope} onChange={(e) => set({ scope: e.target.value as KycBindingForm['scope'], ...(e.target.value === 'country' ? { marketId: '' } : { overridable: false }) })}>
            <option value="country">{t('denable.scope.country')}</option>
            <option value="market" disabled={markets.length === 0}>{t('denable.scope.market')}</option>
          </Select>
        </Field>
        {form.scope === 'market' && (
          <Field label={t('w6.market.field')} htmlFor="kyc-binding-market">
            <Select id="kyc-binding-market" value={form.marketId} onChange={(e) => set({ marketId: e.target.value })}>
              <option value="">{t('w6.market.choose')}</option>
              {markets.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </Select>
          </Field>
        )}
        <Field label={t('denable.col.category')} htmlFor="kyc-binding-category" hint={t('denable.category.hint')}>
          <Select id="kyc-binding-category" value={form.category} onChange={(e) => set({ category: e.target.value })}>
            <option value="">{t('denable.category.all')}</option>
            {categories.map((c) => (
              <option key={c} value={c}>{v('ride.category', c)}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('denable.col.level')} htmlFor="kyc-binding-level">
          <Select id="kyc-binding-level" value={form.requirement} onChange={(e) => set({ requirement: e.target.value as KycBindingForm['requirement'] })}>
            <option value="REQUIRED">{v('kyc.requirement_level', 'REQUIRED')}</option>
            <option value="OPTIONAL">{v('kyc.requirement_level', 'OPTIONAL')}</option>
          </Select>
        </Field>
      </FormGrid>
      {form.scope === 'country' && (
        <CheckRow id="kyc-binding-overridable" checked={form.overridable} onChange={(v) => set({ overridable: v })}>{t('denable.overridable.label')}</CheckRow>
      
      )}
    </EditorFrame>
  );
}

function VehicleRuleEditor({ form, onChange, onApply, onCancel }: { form: KycVehicleRuleForm; onChange: (form: KycVehicleRuleForm) => void; onApply: () => void; onCancel: () => void }): ReactElement {
  const { t } = useT();
  const set = (patch: Partial<KycVehicleRuleForm>): void => onChange({ ...form, ...patch });
  return (
    <EditorFrame title={t('denable.editor.vehicleRule')} onApply={onApply} onCancel={onCancel}>
      <FormGrid>
        <Field label={t('denable.col.category')} htmlFor="kyc-rule-category" hint={t('denable.vehicleRule.category.hint')}>
          <TextInput id="kyc-rule-category" className="tnum" value={form.category} autoComplete="off" spellCheck={false} onChange={(e) => set({ category: e.target.value })} />
        </Field>
        <Field label={t('denable.col.vehicleClasses')} htmlFor="kyc-rule-classes" hint={t('denable.vehicleRule.classes.hint')}>
          <TextInput id="kyc-rule-classes" value={form.allowedVehicleTypes} autoComplete="off" spellCheck={false} onChange={(e) => set({ allowedVehicleTypes: e.target.value })} />
        </Field>
        <Field label={t('denable.col.minYear')} htmlFor="kyc-rule-year">
          <TextInput id="kyc-rule-year" className="tnum" inputMode="numeric" value={form.minVehicleYear} onChange={(e) => set({ minVehicleYear: e.target.value })} />
        </Field>
      </FormGrid>
      <Cluster gap={2} wrap>
        <CheckRow id="kyc-rule-inspection" checked={form.requireValidInspection} onChange={(v) => set({ requireValidInspection: v })}>{t('denable.vehicleRule.inspection')}</CheckRow>
        <CheckRow id="kyc-rule-active" checked={form.requireActiveVehicle} onChange={(v) => set({ requireActiveVehicle: v })}>{t('denable.vehicleRule.active')}</CheckRow>
      </Cluster>
    </EditorFrame>
  );
}
